import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Eye, EyeOff, CheckCircle2, XCircle, ArrowRight, Shield } from 'lucide-react';

const UpdatePassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [touched, setTouched] = useState({ password: false, confirm: false });
  const navigate = useNavigate();

  useEffect(() => {
    const urlHash = window.location.hash;
    const params = new URLSearchParams(urlHash.substring(1));
    const access_token = params.get('access_token');
    const refresh_token = params.get('refresh_token');

    if (access_token && refresh_token) {
      supabase.auth.setSession({ access_token, refresh_token }).then(({ error }) => {
        if (error) {

          toast.error('שגיאה באימות, נסה שוב מהקישור');
        } else {
          setSessionReady(true);
        }
      });
    } else {
      toast.error('הקישור לא תקין או שפג תוקפו');
    }
  }, []);

  const passwordValidations = {
    minLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const isPasswordValid = Object.values(passwordValidations).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const canSubmit = isPasswordValid && passwordsMatch && sessionReady;

  const handleUpdatePassword = async () => {
    if (!sessionReady) {
      toast.error('אין סשן פעיל. נסה לרענן את הדף או לפתוח שוב את הקישור מהמייל.');
      return;
    }

    if (!canSubmit) {
      toast.error('אנא ודא שהסיסמה עומדת בכל הדרישות ושהסיסמאות תואמות');
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      if (error.message?.toLowerCase().includes('new password should be different')) {
        toast.error('הסיסמה החדשה חייבת להיות שונה מהסיסמה הקודמת');
      } else if (error.message?.toLowerCase().includes('password should contain at least one character')) {
        toast.error('הסיסמה חייבת לכלול אותיות, מספרים ותווים מיוחדים');
      } else if (error.message?.toLowerCase().includes('weak password')) {
        toast.error('הסיסמה חלשה מדי, נסה לבחור סיסמה חזקה יותר');
      } else {
        toast.error('שגיאה בעדכון הסיסמה');

      }
    } else {
      toast.success('הסיסמה עודכנה בהצלחה!');
      setTimeout(() => navigate('/login'), 1500);
    }

    setLoading(false);
  };

  const ValidationItem = ({ valid, text }: { valid: boolean; text: string }) => (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex items-center gap-2"
    >
      {valid ? (
        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
      ) : (
        <XCircle className="h-4 w-4 text-gray-300 flex-shrink-0" />
      )}
      <span className={`text-[13px] ${valid ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
        {text}
      </span>
    </motion.div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-50 px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4 }}
        className="max-w-md w-full bg-white rounded-3xl shadow-xl overflow-hidden"
      >
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
            className="mx-auto h-16 w-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4"
          >
            <Shield className="h-8 w-8 text-white" />
          </motion.div>
          <h2 className="text-[24px] font-bold text-white mb-2">עדכון סיסמה</h2>
          <p className="text-blue-100 text-[14px]">
            צור סיסמה חזקה לאבטחת החשבון שלך
          </p>
        </div>

        <div className="p-6 space-y-6">
          {!sessionReady && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 flex items-start gap-3"
            >
              <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">!</span>
              </div>
              <div>
                <p className="text-[14px] text-amber-900 font-semibold mb-1">ממתין לאימות</p>
                <p className="text-[13px] text-amber-700">
                  הקישור בתהליך טעינה, המתן רגע...
                </p>
              </div>
            </motion.div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-[14px] font-semibold text-gray-700 mb-2">
                סיסמה חדשה
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="הזן סיסמה חדשה"
                  className={`w-full px-4 py-3.5 pr-12 bg-gray-50 border-2 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                    touched.password && !isPasswordValid
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-gray-200'
                  }`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => setTouched({ ...touched, password: true })}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <AnimatePresence>
              {password.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-gray-50 rounded-xl p-4 space-y-2"
                >
                  <p className="text-[13px] font-bold text-gray-700 mb-3">דרישות סיסמה:</p>
                  <ValidationItem valid={passwordValidations.minLength} text="לפחות 8 תווים" />
                  <ValidationItem valid={passwordValidations.hasUpperCase} text="אות גדולה באנגלית (A-Z)" />
                  <ValidationItem valid={passwordValidations.hasLowerCase} text="אות קטנה באנגלית (a-z)" />
                  <ValidationItem valid={passwordValidations.hasNumber} text="מספר אחד לפחות (0-9)" />
                  <ValidationItem valid={passwordValidations.hasSpecial} text="תו מיוחד (!@#$%^&*)" />
                </motion.div>
              )}
            </AnimatePresence>

            <div>
              <label className="block text-[14px] font-semibold text-gray-700 mb-2">
                אימות סיסמה
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="הזן את הסיסמה שוב"
                  className={`w-full px-4 py-3.5 pr-12 bg-gray-50 border-2 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                    touched.confirm && confirmPassword && !passwordsMatch
                      ? 'border-red-300 focus:ring-red-500'
                      : touched.confirm && passwordsMatch
                      ? 'border-green-300 focus:ring-green-500'
                      : 'border-gray-200'
                  }`}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={() => setTouched({ ...touched, confirm: true })}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              <AnimatePresence>
                {confirmPassword && !passwordsMatch && touched.confirm && (
                  <motion.p
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="text-[13px] text-red-600 mt-2 flex items-center gap-1"
                  >
                    <XCircle className="h-4 w-4" />
                    הסיסמאות אינן תואמות
                  </motion.p>
                )}
                {confirmPassword && passwordsMatch && (
                  <motion.p
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-[13px] text-green-600 mt-2 flex items-center gap-1"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    הסיסמאות תואמות
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="space-y-3 pt-2">
            <motion.button
              whileHover={canSubmit ? { scale: 1.02 } : {}}
              whileTap={canSubmit ? { scale: 0.98 } : {}}
              onClick={handleUpdatePassword}
              disabled={loading || !canSubmit}
              className={`w-full py-3.5 rounded-xl font-bold text-[16px] shadow-lg transition-all flex items-center justify-center gap-2 ${
                canSubmit
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:shadow-xl active:scale-95'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>מעדכן סיסמה...</span>
                </>
              ) : (
                <>
                  <Lock className="h-5 w-5" />
                  <span>עדכן סיסמה</span>
                </>
              )}
            </motion.button>

            <button
              onClick={() => {
                localStorage.clear();
                sessionStorage.clear();
                window.location.replace('/login');
              }}
              className="w-full py-3 text-gray-600 hover:text-gray-900 font-medium text-[15px] transition-colors flex items-center justify-center gap-2"
            >
              <ArrowRight className="h-4 w-4" />
              חזרה לעמוד ההתחברות
            </button>
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-t border-gray-200">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Shield className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="text-[13px] font-semibold text-gray-900 mb-1">
                טיפ לאבטחה
              </p>
              <p className="text-[12px] text-gray-600 leading-relaxed">
                אל תשתמש באותה סיסמה במספר אתרים. השתמש במנהל סיסמאות לשמירה מאובטחת.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default UpdatePassword;
